import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const ProductCard = ({ product }) => {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();

  const inWishlist = isInWishlist(product.id);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const result = await addToCart(product.id, 1);
    if (result.success) {
      toast.success(`${product.name} added to cart`);
    } else {
      toast.error(result.error);
    }
  };

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error('Please login to add items to wishlist');
      return;
    }

    if (inWishlist) {
      const result = await removeFromWishlist(product.id);
      if (result.success) {
        toast.success('Removed from wishlist');
      }
    } else {
      const result = await addToWishlist(product.id);
      if (result.success) {
        toast.success('Added to wishlist');
      }
    }
  };

  return (
    <Link to={`/products/${product.id}`} className="block" data-testid={`product-card-${product.id}`}>
      <div className="glass-card p-4 h-full flex flex-col">
        <div className="relative mb-4">
          <img
            src={product.image_url || 'https://images.pexels.com/photos/5922101/pexels-photo-5922101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'}
            alt={product.name}
            className="product-image w-full h-48 object-cover rounded-lg"
          />
          <button
            onClick={handleWishlistToggle}
            className={`absolute top-2 right-2 p-2 rounded-full transition-all duration-200 ${
              inWishlist ? 'bg-[#00F0FF] text-black' : 'bg-black/50 text-white hover:bg-black/70'
            }`}
            data-testid={`wishlist-toggle-${product.id}`}
          >
            <Heart className={`w-4 h-4 ${inWishlist ? 'fill-current' : ''}`} />
          </button>
          {product.stock <= 10 && product.stock > 0 && (
            <span className="absolute top-2 left-2 badge-cyan">Low Stock</span>
          )}
          {product.stock === 0 && (
            <span className="absolute top-2 left-2 bg-[#FF3B30]/20 text-[#FF3B30] px-2 py-1 rounded-full text-xs font-medium">
              Out of Stock
            </span>
          )}
        </div>

        <div className="flex-1 flex flex-col">
          <span className="text-[#00F0FF] text-xs font-medium uppercase tracking-wider mb-1">
            Research
          </span>
          <h3 className="text-white font-semibold text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {product.name}
          </h3>
          <p className="text-[#A1A7B5] text-sm mb-3 line-clamp-2">{product.description}</p>

          {product.purity && (
            <div className="flex items-center space-x-4 mb-3 text-xs text-[#A1A7B5]">
              <span>Purity: {product.purity}</span>
            </div>
          )}

          <div className="mt-auto flex items-center justify-between">
            <span className="text-white font-bold text-xl" data-testid={`product-price-${product.id}`}>
              ${product.price.toFixed(2)}
            </span>
            <button
              onClick={handleAddToCart}
              disabled={product.stock === 0}
              className={`p-3 rounded-lg transition-all duration-200 ${
                product.stock === 0
                  ? 'bg-[#1E2532] text-[#A1A7B5] cursor-not-allowed'
                  : 'bg-[#00F0FF] text-black hover:bg-[#00B8D9]'
              }`}
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingCart className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
